import {inject, Injectable, OnInit} from '@angular/core';
import {
    Auth,
    createUserWithEmailAndPassword,
    GoogleAuthProvider,
    OAuthProvider,
    onAuthStateChanged,
    sendEmailVerification as fbSendEmailVerification,
    sendPasswordResetEmail,
    signInWithCredential,
    signInWithEmailAndPassword,
    signOut,
    updateProfile as fbUpdateProfile
} from '@angular/fire/auth';
import {IUser} from '../interfaces/IUser';
import {BehaviorSubject, EMPTY, from, Observable, retry, timer} from 'rxjs';
import {Platform} from '@ionic/angular';
import {HttpClient} from '@angular/common/http';
import {environment} from '../../environments/environment';
import {SocialLogin} from '@capgo/capacitor-social-login';
import {UtilityService} from './utility.service';
import {catchError, finalize, map, switchMap, tap} from 'rxjs/operators';
import {JwtHelperService} from '@auth0/angular-jwt';
import {StorageService} from './storage.service';

const TOKEN_KEY = 'access_token';
const USER_KEY = 'user';

@Injectable({
    providedIn: 'root'
})
export class AuthService implements OnInit {
    urlApi = environment.urlApi;
    public user: BehaviorSubject<IUser> = new BehaviorSubject<IUser>(null);
    public authenticationState: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
    private readonly http = inject(HttpClient);
    private readonly storage = inject(StorageService);
    private readonly utilityService = inject(UtilityService);
    private readonly platform = inject(Platform);
    private readonly jwtHelper = inject(JwtHelperService);
    private readonly auth = inject(Auth);


    constructor() {
        this.isAuthenticated();
        this.listenToAuthState();
    }

    ngOnInit() {
    }

    private listenToAuthState() {
        onAuthStateChanged(this.auth, user => {
            if (user) {
                from(user.getIdToken()).pipe(
                    switchMap(token => this.http.post<{ access_token: string; user: any }>(
                        `${this.urlApi}${environment.USER_AUTHENTICATE}`,
                        // Se esiste, includo anche il displayName attuale del profilo
                        user.displayName ? {firebaseToken: token, displayName: user.displayName} : {firebaseToken: token}
                    )),
                    switchMap(res => from(this.setToken(res.access_token)).pipe(map(() => res))),
                    switchMap(res => from(this.setUser(res.user)).pipe(map(() => res.user))),
                    tap(u => {
                        this.user.next(u);
                        this.authenticationState.next(true);
                    }),
                    catchError(err => {
                        console.error('Errore durante autenticazione:', err);
                        this.authenticationState.next(false);
                        return EMPTY;
                    })
                ).subscribe();
            } else {
                from(this.storage.remove(USER_KEY)).pipe(
                    switchMap(() => from(this.storage.remove(TOKEN_KEY))),
                    tap(() => {
                        this.authenticationState.next(false);
                        this.user.next(null);
                    }),
                    catchError(err => {
                        console.error('Errore durante cleanup storage:', err);
                        this.authenticationState.next(false);
                        this.user.next(null);
                        return EMPTY;
                    })
                ).subscribe();
            }
        });
    }


    async initializeSocialLogin() {
        await SocialLogin.initialize({
            google: {
                webClientId: '606740515057-qlb4c7vk0seocre906n2eh9sfklgqtid.apps.googleusercontent.com',
                iOSClientId: environment.firebaseConfig.iosClientId,
                mode: 'online'
            }
        });
    }

    async googleLogin() {
        try {
            const {result} = await SocialLogin.login({
                provider: 'google',
                options: {scopes: ['email', 'profile']}
            });

            if (result.responseType === 'online' && result.idToken) {
                const credential = GoogleAuthProvider.credential(result.idToken);
                await signInWithCredential(this.auth, credential);
            } else {
                console.error('Login Google fallito o configurazione errata:', result);
            }
        } catch (err) {
            console.error('My error googleLogin 2 :', err);
        }
    }

    async continueAppleLogin() {
        try {
            const {result} = await SocialLogin.login({
                provider: 'apple',
                options: {scopes: ['name', 'email']} // Capgo: niente nonce documentata
            });

            // Prendi SEMPRE il JWT
            const idToken = result?.accessToken?.token ?? result?.idToken;
            if (!idToken || !idToken.startsWith('eyJ')) {
                throw new Error('Apple Sign-In: idToken JWT non presente.');
            }

            const appleProvider = new OAuthProvider('apple.com');

            // 🔴 NIENTE rawNonce qui, perché Capgo non la inserisce nell’ID token
            const credential = appleProvider.credential({idToken});

            const authResult = await signInWithCredential(this.auth, credential);

            const displayName =
                (result?.profile?.givenName && result?.profile?.familyName)
                    ? `${result.profile.givenName} ${result.profile.familyName}`.trim()
                    : (result?.profile?.givenName ?? this.takeInitialEmail(authResult.user.email));

            await this.updateProfile(displayName);
        } catch (err) {
            console.error('Errore Apple Login:', err);
            this.utilityService.presentAlertErrorGeneric(1003, err);
        }
    }


    async register({username, email, password}) {
        const credential = await createUserWithEmailAndPassword(this.auth, email, password);

        // Aggiorna subito il profilo Firebase
        await this.updateProfile(username);

        // Forza il refresh dell’utente lato client
        await this.auth.currentUser?.reload();

        // Prendi un token fresco (force refresh = true per sicurezza)
        const freshToken = await this.auth.currentUser?.getIdToken(true);

        // Chiama esplicitamente la tua API includendo anche il displayName
        if (freshToken) {
            this.login(freshToken, username);
        }

        const userDB: IUser = {
            uid: credential.user.uid,
            email: credential.user.email,
            displayName: username,
            imageUrl: ''
        };

        return userDB;
    }

    async updateProfile(username, img?) {
        const photoUrl = img ?? '';
        const user = this.auth.currentUser;
        if (user) {
            await fbUpdateProfile(user, {
                displayName: username,
                photoURL: photoUrl
            }).catch(error => {
                this.utilityService.presentAlertErrorGeneric(1002, error);
            });
        }
    }

    async sendEmailVerification(presentToast?) {
        const user = this.auth.currentUser;

        if (user) {
            await fbSendEmailVerification(user).then(() => {
                if (presentToast) {
                    this.utilityService.presentToastSuccess('Email di verifica inviata, controlla la tua posta anche in spam e clicca sul link ricevuto');
                }
            }).catch(() => {
                this.utilityService.presentToastError('Stai facendo troppe richieste, attendi qualche minuto e riprova perfavore.');
            });
        }
    }

    async signIn({email, password}) {
        const result = await signInWithEmailAndPassword(this.auth, email, password);
        const token = await result.user.getIdToken();
        this.login(token);
    }

    login(firebaseToken: string, displayName?: string): void {
        const payload: any = {firebaseToken};
        if (displayName && displayName.trim().length > 0) {
            payload.displayName = displayName.trim();
        }

        this.http
            .post<{ access_token: string; user: any }>(
                `${environment.urlApi}${environment.USER_AUTHENTICATE}`,
                payload
            )
            .subscribe({
                next: res => {
                    this.setToken(res.access_token)
                        .then(() => this.setUser(res.user))
                        .then(() => {
                            this.user.next(res.user);
                            this.authenticationState.next(true);
                        })
                        .catch(err => {
                            console.error('Errore durante setToken/setUser:', err);
                            this.authenticationState.next(false);
                        });
                },
                error: err => {
                    console.error('Errore HTTP login:', err);
                    this.authenticationState.next(false);
                }
            });
    }

    async setToken(token: string) {
        await this.platform.ready();
        await this.storage.set(TOKEN_KEY, token);
    }

    async setUser(user: any) {
        await this.platform.ready();
        await this.storage.set(USER_KEY, user);
    }

    async signOut() {
        return signOut(this.auth);
    }


    takeInitialEmail(email: string): string {
        const match = /[^@]*/.exec(email);
        return match ? match[0] : '';
    }

    resetPassword(emailAddress: string) {
        sendPasswordResetEmail(this.auth, emailAddress)
            .then(() => {
                // Successo (puoi eventualmente mostrare un messaggio)
            })
            .catch(error => {
                this.utilityService.presentAlertErrorGeneric(1004, error);
            });
    }

    getWordsBlackList(userNameScelto: string): Observable<any> {
        const link = this.urlApi + environment.UTILITY_GET_WORDS_BLACKLIST;
        return this.http.get<any>(link).pipe(
            retry({count: 5, delay: () => timer(1000)}),
            tap(resultBlackList => {
                resultBlackList.blackList.forEach((word: string) => {
                    if (userNameScelto.toUpperCase().includes(word.toUpperCase())) {
                        throw new Error(
                            'Il nome utente scelto contiene una o più parole non consentite, perfavore modificalo'
                        );
                    }
                });
            })
        );
    }

    getWordsBlackListDue(): Observable<any> {
        const link = this.urlApi + environment.UTILITY_GET_WORDS_BLACKLIST;
        return this.http.get<any>(link).pipe(
            retry({count: 5, delay: () => timer(1000)})
        );
    }


    getUsernameList(userNameScelto): Observable<any> {
        const link = this.urlApi + environment.USER_GET_USERNAME_LIST;
        const data = {
            displayName: userNameScelto
        };
        return this.http.post<any>(link, data);
    }

    deleteAccount() {
        this.deleteAccountFromMyDB().subscribe(result => {
            this.utilityService.presentToastSuccess('Il tuo account è stato eliminato!');
            this.signOut();
        });
    }

    deleteAccountFromMyDB(): Observable<any> {
        return this.http
            .post<any>(this.urlApi + environment.USER_DELETE, {})
            .pipe(
                retry({count: 5, delay: () => timer(1000)})
            );
    }

    updateUsername(username: string): Observable<any> {
        return this.http
            .post<any>(this.urlApi + environment.USER_UPDATE_DISPLAY_NAME, {displayName: username})
            .pipe(
                retry({count: 5, delay: () => timer(1000)})
            );
    }

    async uploadImage(file: Blob | File): Promise<void> {
        const myLink = this.urlApi + environment.USER_UPDATE_PROFILE_IMAGE;
        const formData = new FormData();
        formData.append('imageUrl', file);

        this.http.post<string>(myLink, formData).pipe(
            retry({count: 5, delay: () => timer(1000)}),
            finalize(() => {
                // eventuale cleanup
            })
        ).subscribe({
            next: res => {
                const user = this.user.getValue();
                user.imageUrl = res;
                this.updateProfile(user.displayName, res)
                    .then(() => { /* ok */
                    })
                    .catch(err => {
                        this.utilityService.presentAlertErrorGeneric(1010, err);
                        this.user.next(user);
                    });
            },
            error: err => {
                this.utilityService.presentAlertErrorGeneric(137, err);
            }
        });
    }

    async isAuthenticated(): Promise<void> {
        await this.platform.ready();
        try {
            const token = await this.storage.get(TOKEN_KEY);
            const isExpired = this.jwtHelper.isTokenExpired(token);

            this.authenticationState.next(!isExpired);
            if (isExpired) {
                await this.storage.remove(USER_KEY);
            }
            await this.getUser();
        } catch (err) {
            console.error('Errore in isAuthenticated():', err);
            this.authenticationState.next(false);
        }
    }

    async getUser() {
        this.platform.ready().then(async () => {
            const user = await this.storage.get(USER_KEY);
            this.user.next(user);
        });
    }

    // auth.service.ts (aggiunte in coda alla classe, senza rimuovere nulla)
    async getToken(): Promise<string | null> {
        try {
            await this.platform.ready();
            const token = await this.storage.get('access_token'); // stesso TOKEN_KEY
            if (typeof token === 'string' && token.trim().length > 0) {
                return token.trim();
            }
            return null;
        } catch (err) {
            console.error('Errore nel recupero del token:', err);
            return null;
        }
    }

    /**
     * Ritorna true se abbiamo un token valido in storage e lo JWT non risulta scaduto.
     * Non forza il refresh qui perché il tuo flusso lo gestisce altrove.
     */
    async hasValidToken(): Promise<boolean> {
        try {
            const token = await this.getToken();
            if (!token) {
                return false;
            }
            return !this.jwtHelper.isTokenExpired(token);
        } catch (err) {
            console.error('Errore nella validazione del token:', err);
            return false;
        }
    }

}
